import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useStyles } from './TaskScore.styles';
import { ExamTask, ExamScoringSchema, CriterionType } from '../../../models/assessment.types';
import { SampleAssessmentPill } from '../../SampleAssessmentPill/SampleAssessmentPill.component';
import { AssessmentSessionProcessTypeId, Status } from '../../../services/exams/assessmentSession/assessments.types';
import { getPillTooltip } from '../../../utils/assessmentQuality';
import {
  ToleranceRangeCriterion,
  ToleranceRangeTask,
} from '../../../pages/Sessions/SessionsParametersDialog/ToleranceRange/ToleranceRange.types';
import TooltipOne from '../../TooltipOne/TooltipOne.component';
import { SolutionViewMode } from '../../../pages/Solution/Solution.component';
import { useScoring } from '../../../Context/Scoring.context';
import { Prompt } from '../../Prompt/Prompt.component';
import { ExaminerFunction } from '../../../pages/Assessment/Assessment.types';
import _ from 'lodash';
import { CriterionTypeIcon } from './CriterionTypeIcon.component';
import { highContrastMode } from '../../../utils/highContrastChecker';

interface TaskScoreProps {
  mode: SolutionViewMode;
  examTask: ExamTask;
  handleSelectTask: (taskNumber: string) => void;
  handleSelectCriterion: (taskNumber: string, examScoringSchemaId: string) => void;
  examTaskGroupSolutionScoreState: Status;
  processTypeId: AssessmentSessionProcessTypeId;
  taskToleranceRange: ToleranceRangeTask | null;
  activeCriterionIndex?: number;
  isPreviousCriteria?: boolean;
  examinerFunction: ExaminerFunction;
}

export const TaskScore: React.FC<TaskScoreProps> = (props) => {
  const classes = useStyles();

  const [maxTaskScore, setMaxTaskScore] = useState<number>();
  const [totalTaskScore, setTotalTaskScore] = useState<number | null>(null);
  const [totalAssessmentScore, setTotalAssessmentScore] = useState<number | null>(null);
  const [criterionChangeContinueCallback, setCriterionChangeContinueCallback] = useState<(() => void) | null>(null);
  const [criterionChangeConfirmationModalOpen, setCriterionChangeConfirmationModalOpen] = useState<boolean>(false);
  const [examScoringSchemas, setExamScoringSchemas] = useState<ExamScoringSchema[] | null>(null);

  const {
    activeTask,
    activeCriterion,
    isCommentForCurrentScoreRequired,
    isCommentForCurrentScoreProvided,
    criteriaSetups,
  } = useScoring();

  const border = highContrastMode ? 'solid 1px white' : 'none';

  useEffect(() => {
    if (activeTask && activeCriterion && props.isPreviousCriteria !== undefined) {
      const schemasLength = activeTask.examScoringSchemas.findIndex(
        (e) => e.examScoringSchemaId === activeCriterion.examScoringSchemaId,
      );
      const schemas: ExamScoringSchema[] = [];
      activeTask.examScoringSchemas.slice(0, schemasLength).map((scoringSchema: ExamScoringSchema) => {
        if (scoringSchema.examScoringSchemaId === activeCriterion.examScoringSchemaId) {
          return;
        }
        schemas.push(scoringSchema);
      });
      return setExamScoringSchemas(schemas);
    } else setExamScoringSchemas(props.examTask.examScoringSchemas);
  }, [activeTask, activeCriterion, props.examTask, props.isPreviousCriteria]);

  const getScore = () => {
    let score = 0;
    let maxScore = 0;
    let nonEmptyScores = false;
    let hasSampleScore = false;

    props.examTask.examScoringSchemas.forEach((schema: ExamScoringSchema) => {
      score = score + (schema.score ? schema.score : 0);
      maxScore = maxScore + Math.max(...schema.examScoreValues.map((e) => e.score));
      if (schema.score !== null) nonEmptyScores = true;
    });

    const sampleScoreSum = props.examTask.examScoringSchemas.reduce((sum: number, scoringSchema: ExamScoringSchema) => {
      if (scoringSchema.sampleScore !== null) hasSampleScore = true;
      return sum + (scoringSchema.sampleScore !== null ? scoringSchema.sampleScore : 0);
    }, 0);
    if (nonEmptyScores) setTotalTaskScore(score);
    else setTotalTaskScore(null);
    setMaxTaskScore(maxScore);
    setTotalAssessmentScore(hasSampleScore ? sampleScoreSum : null);
  };

  const needToDisplayRequiredCommentModal = () => {
    return (
      isCommentForCurrentScoreRequired &&
      !isCommentForCurrentScoreProvided &&
      !activeTask?.noAnswer &&
      props.examinerFunction !== ExaminerFunction.KK
    );
  };

  const handleTaskClick = useCallback(() => {
    const continueCallback = () => {
      props.handleSelectTask(props.examTask.taskNumber);
    };
    const need = needToDisplayRequiredCommentModal();
    if (need) {
      setCriterionChangeConfirmationModalOpen(true);
      setCriterionChangeContinueCallback(() => () => {
        continueCallback();
      });
    } else {
      continueCallback();
    }
  }, [isCommentForCurrentScoreRequired, isCommentForCurrentScoreProvided, props.handleSelectTask]);

  const handleContinueCriterionChange = () => {
    criterionChangeContinueCallback && criterionChangeContinueCallback();
    setCriterionChangeContinueCallback(null);
    setCriterionChangeConfirmationModalOpen(false);
  };

  const handleConfirmCommentAdding = () => {
    setCriterionChangeConfirmationModalOpen(false);
    setCriterionChangeContinueCallback(null);
  };

  const handleSelectCriterium = useCallback(
    (scoringSchema: ExamScoringSchema) => {
      const continueCallback = () => {
        props.handleSelectCriterion(props.examTask.taskNumber, scoringSchema.examScoringSchemaId);
      };
      if (needToDisplayRequiredCommentModal()) {
        setCriterionChangeConfirmationModalOpen(true);
        setCriterionChangeContinueCallback(() => () => {
          continueCallback();
        });
      } else {
        continueCallback();
      }
    },
    [
      isCommentForCurrentScoreRequired,
      isCommentForCurrentScoreProvided,
      props.handleSelectCriterion,
      props.examTask.taskNumber,
    ],
  );

  useEffect(() => {
    if (props.examTask) {
      getScore();
    }
  }, [props.examTask]);

  const leadingCriterionsIds = useMemo((): string[] => {
    return criteriaSetups
      ? _.uniq(criteriaSetups.filter((e) => e.leadingSchemaId).map((item) => item.leadingSchemaId))
      : [];
  }, [criteriaSetups]);

  const dependantCriterionsIds = useMemo((): string[] => {
    return criteriaSetups
      ? _.uniq(criteriaSetups.filter((e) => e.leadingSchemaId).map((item) => item.dependentSchemaId))
      : [];
  }, [criteriaSetups]);

  const isLeadingCriterion = (criterion: ExamScoringSchema) => {
    return leadingCriterionsIds.some((e) => e === criterion.examScoringSchemaId);
  };

  const isDependentCriterion = (criterion: ExamScoringSchema) => {
    return dependantCriterionsIds.some((e) => e === criterion.examScoringSchemaId);
  };
  return (
    <>
      {props.examTask && (
        <div className={classes.taskScore}>
          <div className={classes.score}>
            <div className={classes.header}>
              <h2
                className={classes.headerName}
                onClick={() => {
                  handleTaskClick();
                }}
              >
                Zadanie {props.examTask.taskNumber}
              </h2>
              <div className={classes.sampleAssessmentScoreContainer}>
                {props.examTaskGroupSolutionScoreState === Status.Closed && totalAssessmentScore !== null && (
                  <SampleAssessmentPill
                    sampleAssessmentPoints={totalAssessmentScore}
                    tooltip={getPillTooltip(
                      props.processTypeId,
                      props.taskToleranceRange?.toleranceRange?.toleranceRangeType,
                      props.taskToleranceRange?.toleranceRange?.toleranceRangeValue,
                    )}
                    assessmentQuality={props.examTask.assessmentQuality}
                    processTypeId={props.processTypeId}
                  />
                )}
                <div className={classes.headerScore}>
                  <span style={{ whiteSpace: 'nowrap' }}>
                    {props.mode === SolutionViewMode.sampleAssessmentGroupAssignment
                      ? ''
                      : totalTaskScore !== null
                      ? totalTaskScore
                      : '-'}
                    /{props.mode === SolutionViewMode.sampleAssessmentGroupAssignment ? '-' : maxTaskScore}
                  </span>
                </div>
              </div>
            </div>
            <ul className={classes.scoreList} style={{ border }}>
              {examScoringSchemas &&
                examScoringSchemas.map((score: ExamScoringSchema) => {
                  const criterionToleranceRange = props.taskToleranceRange?.criterion.find(
                    (criterionData: ToleranceRangeCriterion) => criterionData.criterionId === score.examScoringSchemaId,
                  );
                  const showQualityPill =
                    props.examTaskGroupSolutionScoreState === Status.Closed && score.sampleScore !== null;

                  const isLeading = isLeadingCriterion(score);

                  return (
                    <React.Fragment
                      key={
                        props.examTask.taskNumber +
                        score.criterionName +
                        score.criterionNumber +
                        score.examScoringSchemaId
                      }
                    >
                      <div
                        onClick={() => {
                          handleSelectCriterium(score);
                        }}
                        className={[classes.taskScore, showQualityPill ? classes.rowWithQualityPill : undefined].join(
                          ' ',
                        )}
                        style={{ border }}
                      >
                        <div className={classes.score}>
                          <ul className={classes.scoreList}>
                            <li>
                              <div style={{ position: 'absolute', left: '0px' }}>
                                {isLeading && (
                                  <TooltipOne title="Kryterium wiodące" placement="top">
                                    <div>
                                      <CriterionTypeIcon criterionType={CriterionType.leadingCriterion} />
                                    </div>
                                  </TooltipOne>
                                )}
                              </div>

                              <TooltipOne title={score.criterionName} placement="left">
                                <div>
                                  {score.label} {score.criterionName}
                                </div>
                              </TooltipOne>
                            </li>
                          </ul>
                        </div>
                        <div className={classes.sampleAssessmentScoreContainer}>
                          {showQualityPill && (
                            <SampleAssessmentPill
                              sampleAssessmentPoints={score.sampleScore!}
                              assessmentQuality={score.assessmentQuality}
                              processTypeId={props.processTypeId}
                              tooltip={
                                (showQualityPill
                                  ? getPillTooltip(
                                      props.processTypeId,
                                      criterionToleranceRange?.toleranceRange?.toleranceRangeType,
                                      criterionToleranceRange?.toleranceRange?.toleranceRangeValue,
                                    )
                                  : '') as string
                              }
                            />
                          )}
                          <span style={{ padding: '12px 12px 12px 10px', whiteSpace: 'nowrap' }}>
                            {props.mode === SolutionViewMode.sampleAssessmentGroupAssignment ? '' : score.score}/
                            {props.mode === SolutionViewMode.sampleAssessmentGroupAssignment
                              ? '-'
                              : Math.max(...score.examScoreValues.map((e) => e.score))}
                          </span>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
            </ul>
          </div>
        </div>
      )}
      <Prompt
        type="confirmation"
        size="sm"
        open={criterionChangeConfirmationModalOpen}
        onConfirm={handleConfirmCommentAdding}
        onCancel={handleContinueCriterionChange}
        mainActionButtonText="TAK"
        cancelButtonText="NIE"
        isBold={true}
        mainText={
          <>
            Liczba przyznanych punktów do kryterium
            <div style={{ fontSize: 'smaller' }}>{activeCriterion?.criterionName}</div> wymaga komentarza.
          </>
        }
        subTexts={[
          {
            text: (
              <>
                Wymuszony komentarz należy wpisać korzystając z głównego paska komentarzy w górnej części ekranu oceniania. <br />
                Czy chcesz go wpisać teraz?
              </>
            ),
          },
        ]}
      />
    </>
  );
};
